import { Singleton } from "annotation";
import environment from "environment";
import ProxyService from "./Proxy";
import TUKIncome from "../entity/tuk/TUKIncome";
import TUKExpense from "../entity/tuk/TUKExpense";
import EntityAware from "../entity/EntityAware";
import Parameter from "./Parameter";
import TUKDashboardSummary from "../entity/tuk/TUKDashboardSummary";
import TUKDashboardAnalytics from "../entity/tuk/TUKDashboardAnalytics";
import TUKMasterIncome from "../entity/tuk/TUKMasterIncome";
import TUKConfig from "../entity/tuk/TUKConfig";
import TUKMasterExpense from "../entity/tuk/TUKMasterExpense";
import TUKBendaharaItemAccess from "../entity/tuk/TUKBendaharaItemAccess";
import TUKReportSummary from "../entity/tuk/TUKReportSummary";

@Singleton
export default class TUKService extends ProxyService {

  public api: string = environment.TUK_API;

  public serializer: EntityAware = TUKIncome;

  public expenseSerializer: EntityAware = TUKExpense;

  public masterIncome: EntityAware = TUKMasterIncome;

  public masterExpense: EntityAware = TUKMasterExpense;

  public dashboardSummary: EntityAware = TUKDashboardSummary;

  public dashboardAnalytic: EntityAware = TUKDashboardAnalytics;

  public configSerializer: EntityAware = TUKConfig;

  public bendaharaConfig: EntityAware = TUKConfig;

  public bendaharaItemAccess: EntityAware = TUKBendaharaItemAccess;

  public reportSummarySerializer: EntityAware = TUKReportSummary;

  public getDashboardSummary(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/dashboard/summary`, params).serializeData(this.dashboardSummary);
    return this.request(parameter);
  }

  public getDashboardAnalytics(range: string = "weekly"): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/dashboard/analytics`, { range }).serializeData(this.dashboardAnalytic);
    return this.request(parameter);
  }

  public getConfigOptions(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/config/options`, params);
    return this.request(parameter);
  }


  public getIncomeList(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/pemasukan`, params).serializeData(this.serializer);
    return this.request(parameter);
  }

  public deleteIncome(id: number): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/pemasukan/${id}`).setMethod(Parameter.methods.DELETE);
    return this.request(parameter);
  }

  public createIncome(data: TUKIncome): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/pemasukan`).setMethod(Parameter.methods.POST).serializeData(this.serializer);
    return this.request(parameter);
  }

  public updateIncome(id: number, data: TUKIncome): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/pemasukan/${id}`).setMethod(Parameter.methods.PUT).serializeData(this.serializer);
    return this.request(parameter);
  }

  /** Expense Methods **/

  public getExpenseList(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/pengeluaran`, params).serializeData(this.expenseSerializer);
    return this.request(parameter);
  }

  public deleteExpense(id: number): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/pengeluaran/${id}`).setMethod(Parameter.methods.DELETE);
    return this.request(parameter);
  }

  public createExpense(data: TUKExpense): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/pengeluaran`).setMethod(Parameter.methods.POST).serializeData(this.expenseSerializer);
    return this.request(parameter);
  }

  public updateExpense(id: number, data: TUKExpense): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/pengeluaran/${id}`).setMethod(Parameter.methods.PUT).serializeData(this.expenseSerializer);
    return this.request(parameter);
  }

  /** Master Data Methods **/

  public getMasterIncomeList(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/jenis-pemasukan`, params).serializeData(this.masterIncome);
    return this.request(parameter);
  }

  public createMasterIncome(data: TUKMasterIncome): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/jenis-pemasukan`).setMethod(Parameter.methods.POST).serializeData(this.masterIncome);
    return this.request(parameter);
  }

  public updateMasterIncome(id: number, data: TUKMasterIncome): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/jenis-pemasukan/${id}`).setMethod(Parameter.methods.PUT).serializeData(this.masterIncome);
    return this.request(parameter);
  }

  public deleteMasterIncome(id: number): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/jenis-pemasukan/${id}`).setMethod(Parameter.methods.DELETE);
    return this.request(parameter);
  }

  public getMasterExpenseList(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/jenis-pengeluaran`, params).serializeData(this.masterExpense);
    return this.request(parameter);
  }

  public createMasterExpense(data: TUKMasterExpense): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/jenis-pengeluaran`).setMethod(Parameter.methods.POST).serializeData(this.masterExpense);
    return this.request(parameter);
  }

  public updateMasterExpense(id: number, data: TUKMasterExpense): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/jenis-pengeluaran/${id}`).setMethod(Parameter.methods.PUT).serializeData(this.masterExpense);
    return this.request(parameter);
  }

  public deleteMasterExpense(id: number): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/jenis-pengeluaran/${id}`).setMethod(Parameter.methods.DELETE);
    return this.request(parameter);
  }

  /** Config Methods **/
  public getTukConfig(): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/config/me`).serializeData(this.configSerializer);
    return this.request(parameter);
  }

  public getBendaharaConfigs(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/bendahara-configs`, params).serializeData(this.bendaharaConfig);
    return this.request(parameter);
  }

  public updateBendaharaConfig(id: string, data: TUKConfig): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/bendahara-configs/${id}`).setMethod(Parameter.methods.PUT).serializeData(this.bendaharaConfig);
    return this.request(parameter);
  }

  public updateBendaharaStatus(id: string, isActive: boolean): Promise<any> {
    const data = { is_active: isActive };
    const parameter = new Parameter(data).setUrl(this.api + `/bendahara-configs/status/${id}`).setMethod(Parameter.methods.PUT);
    return this.request(parameter);
  }


  /** Bendahara Item Access Methods **/
  public getBendaharaItemAccess(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/bendahara-item-access`, params).serializeData(this.bendaharaItemAccess);
    return this.request(parameter);
  }

  public batchCreateBendaharaItemAccess(data: any): Promise<any> {
    const parameter = new Parameter(data).setUrl(this.api + `/bendahara-item-access/batch`).setMethod(Parameter.methods.POST);
    return this.request(parameter);
  }

  public deleteBendaharaItemAccess(id: string): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/bendahara-item-access/${id}`).setMethod(Parameter.methods.DELETE);
    return this.request(parameter);
  }

  /** Report Methods **/

  public getReportSummary(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/reports/summary`, params).serializeData(this.reportSummarySerializer);
    return this.request(parameter);
  }

  public getReportIncomeList(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/reports/pemasukan`, params).serializeData(this.serializer);
    return this.request(parameter);
  }

  public exportReportIncome(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/reports/pemasukan/export`, params);
    return this.request(parameter);
  }

  public getReportExpenseList(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/reports/pengeluaran`, params).serializeData(this.expenseSerializer);
    return this.request(parameter);
  }

  public exportReportExpense(params: any = {}): Promise<any> {
    const parameter = new Parameter().setUrl(this.api + `/reports/pengeluaran/export`, params);
    return this.request(parameter);
  }
}
